/**
 * מנגנון ההרשאות.
 *
 * במערכת מנהלים שווי הרשאות בעבודה היומיומית (הכמות נקבעת בהגדרות המערכת). אחד מהם הוא המפתח (owner),
 * שיכול בנוסף לנהל מנהלים, הגדרות מערכת ויומן תקלות (OWNER_PERMISSIONS).
 * לשאר — שאלה אחת: האם המשתמש הוא מנהל פעיל. עם זאת, הפעולות מוגדרות במפורש
 * כדי שכל נקודת קצה תצהיר מה היא דורשת, ושיהיה מקום אחד לשנות אם בעתיד
 * יידרש פיצול הרשאות.
 */
export const ADMIN_PERMISSIONS = [
  'jobs.create',
  'jobs.update',
  'jobs.close',
  'jobs.delete',
  'jobs.archive',
  'contractors.add',
  'contractors.update',
  'registrations.updateCount',
  'changeRequests.decide',
  'access.manage',
  'reports.export',
  'archive.view',
  'activity.view',
  'notifications.manage',
] as const;

export type AdminPermission = (typeof ADMIN_PERMISSIONS)[number];

/** פעולות שרק המפתח רשאי לבצע */
export const OWNER_PERMISSIONS = ['admins.manage', 'settings.manage', 'errors.view', 'system.health'] as const;
export type OwnerPermission = (typeof OWNER_PERMISSIONS)[number];

/** UID של Firebase: 1–128 תווים, ללא רווחים או '/'. */
const UID_RE = /^[^\s/]{1,128}$/;

export type OwnerParseResult = { ok: true; uid: string } | { ok: false; error: string };

/**
 * המפתח (owner) מוגדר בשרת במשתנה OWNER_UID. הוא היחיד שמנהל את רשימת המנהלים.
 * תצורה חסרה או שגויה נכשלת במפורש — השרת לא יפעל בלי בעלים מוגדר.
 */
export function parseOwnerUid(raw: string | undefined | null): OwnerParseResult {
  const uid = (raw ?? '').trim();
  if (!uid) return { ok: false, error: 'OWNER_UID is empty — the developer UID is required' };
  if (!UID_RE.test(uid) || uid.includes(',')) return { ok: false, error: 'OWNER_UID must be a single valid Firebase UID' };
  return { ok: true, uid };
}

/** תמצית רשומת המנהל ב-Firestore, כפי שהיא נחוצה לבדיקת הרשאה. */
export interface AdminRecordForCheck {
  active: boolean;
  removedAt?: unknown;
}

export type AdminDecision = { allowed: true } | { allowed: false; reason: 'no_admin_record' | 'admin_inactive' | 'admin_removed' };

/**
 * ההחלטה המרכזית: משתמש מאומת הוא מנהל רק אם יש לו רשומת מנהל פעילה ב-v2_admins.
 * את הרשומות כותב רק השרת, ורק בפעולה של המפתח (או הסקריפט עם חשבון השירות).
 */
export function decideAdminAccess(record: AdminRecordForCheck | null): AdminDecision {
  if (!record) return { allowed: false, reason: 'no_admin_record' };
  if (record.removedAt) return { allowed: false, reason: 'admin_removed' };
  if (record.active !== true) return { allowed: false, reason: 'admin_inactive' };
  return { allowed: true };
}

/** כל המנהלים שווים: מנהל פעיל רשאי לכל פעולה ברשימה. */
export function adminCan(decision: AdminDecision, permission: AdminPermission): boolean {
  return decision.allowed && (ADMIN_PERMISSIONS as readonly string[]).includes(permission);
}
